import * as z from 'zod'

/**
 * A scanned code: `S:<prescription id>` / `P:<patient id>` from See barcode, or the preparation label
 * Data Matrix `<prescription number>|<hospital no. or Cypro patient id>` (see `getDataMatrixPayload`).
 */
export type ScanCode =
  | {type: 'sequence'; id: string}
  | {type: 'patient'; id: string}
  | {type: 'label'; prescriptionNumber: string; patientIdentifier: string}

export type LabelScanCode = Extract<ScanCode, {type: 'label'}>

interface LabelCandidate {
  internalId: string | null
  patientId: string
  patient?: {hospitalPatientId: string | null} | null | undefined
}

/** Parses a scanned value, or returns null for anything that is not a known Cypro code. */
export function parseScanCode(value: string): ScanCode | null {
  const trimmed = value.trim()

  if (trimmed.includes('|')) {
    const parts = trimmed.split('|')
    const [prescriptionNumber, patientIdentifier] = parts
    if (parts.length !== 2 || !prescriptionNumber || !patientIdentifier) return null
    return {type: 'label', prescriptionNumber, patientIdentifier}
  }

  const [prefix, id, ...rest] = trimmed.split(':')
  if (rest.length || !id || !z.uuid().safeParse(id).success) return null
  if (prefix === 'S') return {type: 'sequence', id}
  if (prefix === 'P') return {type: 'patient', id}
  return null
}

/** Whether a scanned label belongs to this prescription: same prescription number and same patient. */
export function labelMatchesPrescription(label: LabelScanCode, prescription: LabelCandidate) {
  if (!prescription.internalId || prescription.internalId !== label.prescriptionNumber) return false
  return label.patientIdentifier === (prescription.patient?.hospitalPatientId || prescription.patientId)
}

/** The prescriptions a scanned label points at, out of those sharing its prescription number. */
export function findLabelPrescriptions<T extends LabelCandidate>(label: LabelScanCode, candidates: T[]) {
  return candidates.filter(candidate => labelMatchesPrescription(label, candidate))
}
